/**
 * The detail layer in the game (C0-SKILLFX): custom effects pushed beside a move's ink template — at its release
 * (tell, smears, wakes, auras), over a charged wind-up, and on each landed blow — on both layers (ground under the
 * figures, over them). Moves without an entry in `MOVE_DETAIL` get nothing extra.
 */
import { getSkill } from '@nine/shared';
import type { Fx } from '../../fx/registry';
import type { InkLayer, InkScene } from '../samples';
import type { InkKind } from '../palette';
import { tierIndex } from '../../../graphics';
import { detailLife, drawCastDetail, drawHitDetail, drawMoveDetail, HIT_LIFE, setDetailQuality, weightOfMove } from './draw';
import { detailOf } from './moves';
import { oldFxOn } from '../../oldSkillFx';

type CustomFx = Extract<Fx, { type: 'custom' }> & { ground?: boolean };

/** The tier's budget: half the particles on the two low tiers (ต่ำมาก, ต่ำ). */
const syncQuality = (): void => setDetailQuality(tierIndex() <= 1 ? 0.5 : 1);

/** A perf-check switch (scripts/lanes/c0-skillfx/perf.mjs): `window.__noSkillDetail = true` turns the layer off. */
const off = (): boolean =>
  !oldFxOn() || (typeof window !== 'undefined' && !!(window as { __noSkillDetail?: boolean }).__noSkillDetail);

function push(fx: Fx[], sc: InkScene, life: number, draw: (g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer) => void): void {
  if (off()) return;
  for (const layer of ['ground', 'over'] as InkLayer[]) {
    const f: CustomFx = {
      type: 'custom',
      x: sc.x,
      y: sc.y,
      t: 0,
      life,
      ground: layer === 'ground',
      draw: (g, _x, _y, k, ox, oy) => draw(g, k * life, { ...sc, x: sc.x + ox, y: sc.y + oy }, layer),
    };
    fx.push(f);
  }
}

/** A move released: its caster-side detail, the blow `contact` s after the release. */
export function detailRelease(fx: Fx[], skillId: string, sc: InkScene, contact: number): void {
  const d = detailOf(skillId);
  if (!d) return;
  syncQuality();
  const kind = getSkill(skillId).kind as InkKind;
  const w = weightOfMove(skillId);
  push(fx, sc, detailLife(d, contact), (g, t, s, L) => drawMoveDetail(g, t, s, L, d, kind, contact, w));
}

/** A charged move's wind-up over `windup` s. */
export function detailCast(fx: Fx[], skillId: string, sc: InkScene, windup: number): void {
  const d = detailOf(skillId);
  if (!d || windup <= 0) return;
  syncQuality();
  const kind = getSkill(skillId).kind as InkKind;
  const w = weightOfMove(skillId);
  push(fx, sc, windup, (g, t, s, L) => drawCastDetail(g, t / windup, s, L, d, kind, w));
}

/** A landed blow of the move (scene from the attacker to the foe hit). */
export function detailHit(fx: Fx[], skillId: string, sc: InkScene): void {
  const d = detailOf(skillId);
  if (!d) return;
  syncQuality();
  const kind = getSkill(skillId).kind as InkKind;
  const w = weightOfMove(skillId);
  push(fx, sc, HIT_LIFE, (g, t, s, L) => drawHitDetail(g, t, s, L, d, kind, w));
}
